import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, className, containerClassName, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="text-[0.8125rem] font-medium text-ink">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'h-9 w-full rounded-md border bg-surface px-2.5 text-sm text-ink',
          'placeholder:text-muted',
          'transition-[border-color,box-shadow] duration-fast',
          'focus-visible:outline-none focus-visible:border-signal focus-visible:ring-2 focus-visible:ring-signal/15',
          error
            ? 'border-error focus-visible:border-error focus-visible:ring-error/20'
            : 'border-border hover:border-ink/20',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="text-[0.6875rem] text-critical" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[0.6875rem] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
